import {
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Switch,
    TextField,
    Typography,
} from '@mui/material'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../redux/hook'
import { incrementSettingsVersion } from '../../../../redux/slices/facilityMonitoringSlice'
import { useGetAttributes } from '../../../DTStudio/Attributes/hooks/getAttributes'
import { useGetRelations } from '../../../DTStudio/Relations/hooks/getRelation'
import { Applications, AttributeType } from '../../../../constants/constants'
import { enqueueSnackbar } from 'notistack'
import { EntityType, IAttribute, IRelation } from '../../../DTStudio/interfaces'
import { IFacilityMonitoringSettingsType } from '../interfaces'
import {
    getApplicationSettings,
    updateApplicationSettings,
    IHierarchy,
} from '../../../../services/applications'
import {
    findEntityById,
    flattenEntityTemplates,
} from '../../../../utils/EntityUtils'
import { IPreSelectableMappableAttributesOpition } from '../../Intransit/interfaces'
import {
    buildAttributeKey,
    isChartableAndNotHidden,
} from '../../../../utils/AttributeUtils'
import MultiSelectInput from '../../../Common/MultiSelectInput'
import { useTranslation } from 'react-i18next'

const FacilityMonitoringSettings = ({
    hasWriteAccess,
}: {
    hasWriteAccess: boolean
}): ReactElement => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const org = useAppSelector((state) => state.org.org)
    const orgId = org?.id ? org.id : ''
    const navSlice = useAppSelector((state) => state.nav)
    const { navList } = navSlice
    const entityTypes = useMemo(() => {
        return flattenEntityTemplates(navList)
    }, [navList])
    const [enable, setEnable] = useState<boolean>(false)
    const [analyticsDashboardId, setAnalyticsDashboardId] = useState<string>('')
    const [sensorEntityTemplateId, setSensorEntityTemplateId] =
        useState<string>('')
    const [sensorReadingAttributeId, setSensorReadingAttributeId] =
        useState<string>('')
    const [hierarchy, setHierarchy] = useState<IHierarchy[]>([])
    const [numLevels, setNumLevels] = useState<number>(0)
    const [siteEntityTemplateId, setSiteEntityTemplateId] = useState<string>('')
    const [siteLocationAttributeId, setSiteLocationAttributeId] =
        useState<string>('')
    const [siteNameAttributeId, setSiteNameAttributeId] = useState<string>('')
    const [buildingEntityTemplateId, setBuildingEntityTemplateId] =
        useState<string>('')
    const [buildingNameAttributeId, setBuildingNameAttributeId] =
        useState<string>('')
    const [buildingFloorLayoutAttributeId, setBuildingFloorLayoutAttributeId] =
        useState<string>('')
    const [assetEntityTemplateId, setAssetEntityTemplateId] =
        useState<string>('')
    const [assetNameAttributeId, setAssetNameAttributeId] = useState<string>('')
    const [assetXCoordinateAttributeId, setAssetXCoordinateAttributeId] =
        useState<string>('')
    const [assetYCoordinateAttributeId, setAssetYCoordinateAttributeId] =
        useState<string>('')
    const [reportIdAttributeId, setReportIdAttributeId] = useState<string>('')
    const [
        scheduledReportsEntityTemplateId,
        setScheduledReportsEntityTemplateId,
    ] = useState<string>('')
    const [reportEntityTemplateId, setReportEntityTemplateId] =
        useState<string>('')
    const [reportDataFromAttributeId, setReportDataFromAttributeId] =
        useState<string>('')
    const [reportDataUntilAttributeId, setReportDataUntilAttributeId] =
        useState<string>('')
    const [reportingObjectAttributeId, setReportingObjectAttributeId] =
        useState<string>('')
    const [reportURLAttributeId, setReportURLAttributeId] = useState<string>('')
    const [preSelectedChartableAttributes, setPreSelectedChartableAttributes] =
        useState<string>('[]')

    const { data: sensorAttributes = [] } = useGetAttributes(
        orgId,
        sensorEntityTemplateId,
        {
            includeChildAttributes: true,
        },
    )

    const { data: siteAttributes = [] } = useGetAttributes(
        orgId,
        siteEntityTemplateId,
        {
            enabled: !!siteEntityTemplateId,
        },
    )

    const {
        data: siteRelations = [],
        // isError: isLoadingRelationsError,
        // isFetching: isFetchingRelations,
        // isLoading: isLoadingRelations,
    } = useGetRelations(siteEntityTemplateId, orgId)

    const {
        data: buildingAttributes = [],
        // isError: isLoadingAttributesError,
        // isFetching: isFetchingAttributes,
        // isLoading: isLoadingAttributes,
    } = useGetAttributes(orgId, buildingEntityTemplateId)
    const {
        data: buildingRelations = [],
        // isError: isLoadingRelationsError,
        // isFetching: isFetchingRelations,
        // isLoading: isLoadingRelations,
    } = useGetRelations(buildingEntityTemplateId, orgId)

    const {
        data: assetAttributes = [],
        // isError: isLoadingAttributesError,
        // isFetching: isFetchingAttributes,
        // isLoading: isLoadingAttributes,
    } = useGetAttributes(orgId, assetEntityTemplateId)

    const {
        data: assetRelations = [],
        // isError: isLoadingRelationsError,
        // isFetching: isFetchingRelations,
        // isLoading: isLoadingRelations,
    } = useGetRelations(assetEntityTemplateId, orgId)

    const {
        data: reportsAttributes = [],
        // isError: isLoadingReportsAttributesError,
        // isFetching: isFetchingReportsAttributes,
        // isLoading: isLoadingReportsAttributes,
    } = useGetAttributes(orgId, reportEntityTemplateId)

    const parsedPreSelectedChartableAttributes: string[] = useMemo(() => {
        const preselectedAttributes: string[] = JSON.parse(
            preSelectedChartableAttributes,
        )
        /**
         * From the already preselected attributes Inlcude only the attributes of current selected
         *  sensorEntityTemplateId
         */
        return preselectedAttributes.filter(
            (option: string) =>
                !!sensorEntityTemplateId &&
                option.includes(sensorEntityTemplateId),
        )
    }, [preSelectedChartableAttributes, sensorEntityTemplateId])

    const preSelectableChartableAttributesOptions: IPreSelectableMappableAttributesOpition[] =
        []
    const preparePreSelectOptions = (
        attributes: IAttribute[],
        entityTemplateId: string,
    ) => {
        const entityTemplateName =
            findEntityById(entityTypes, entityTemplateId).name || ''

        attributes.forEach((attribute) => {
            if (isChartableAndNotHidden(attribute)) {
                preSelectableChartableAttributesOptions.push({
                    id: buildAttributeKey(attribute.id, entityTemplateId),
                    name: `${attribute.name} (${entityTemplateName})`,
                })
            }
        })
    }
    preparePreSelectOptions(sensorAttributes, sensorEntityTemplateId)

    const onChangeAnalyticsDashboardId = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setAnalyticsDashboardId(event.target.value)
    }

    const onSelectSensor = (event: SelectChangeEvent<string>) => {
        setSensorEntityTemplateId(event.target.value)
        setSensorReadingAttributeId('')
    }

    const onSelectSensorReading = (event: SelectChangeEvent<string>) => {
        setSensorReadingAttributeId(event.target.value)
    }

    const resetSiteAndChildren = () => {
        setSiteLocationAttributeId('')
        setSiteNameAttributeId('')
        setBuildingEntityTemplateId('')
        setBuildingNameAttributeId('')
        setBuildingFloorLayoutAttributeId('')
        setAssetEntityTemplateId('')
        setAssetNameAttributeId('')
        setAssetXCoordinateAttributeId('')
        setAssetYCoordinateAttributeId('')
        setReportIdAttributeId('')
        setSensorEntityTemplateId('')
        setSensorReadingAttributeId('')
    }

    const onSelectSite = (event: SelectChangeEvent<string>) => {
        setSiteEntityTemplateId(event.target.value)
        resetSiteAndChildren()
    }

    const onSelectLocation = (event: SelectChangeEvent<string>) => {
        setSiteLocationAttributeId(event.target.value)
    }

    const onSelectSiteName = (event: SelectChangeEvent<string>) => {
        setSiteNameAttributeId(event.target.value)
    }

    const siteAttributeOptions = siteAttributes.filter(
        (attribute) =>
            attribute.dataType.toLowerCase() === AttributeType.GEO_FENCING,
    )

    const onSelectBuilding = (event: SelectChangeEvent<string>) => {
        setBuildingEntityTemplateId(event.target.value)
        setBuildingNameAttributeId('')
        setBuildingFloorLayoutAttributeId('')
        setAssetEntityTemplateId('')
        setAssetNameAttributeId('')
    }

    const onSelectBuildingName = (event: SelectChangeEvent<string>) => {
        setBuildingNameAttributeId(event.target.value)
    }

    const onSelectBuildingFloorLayout = (event: SelectChangeEvent<string>) => {
        setBuildingFloorLayoutAttributeId(event.target.value)
    }

    const onSelectAssetEntity = (event: SelectChangeEvent<string>) => {
        setAssetEntityTemplateId(event.target.value)
        setAssetNameAttributeId('')
        setAssetXCoordinateAttributeId('')
        setAssetYCoordinateAttributeId('')
        setReportIdAttributeId('')
    }

    const onSelectAssetAttribute = (event: SelectChangeEvent<string>) => {
        setAssetNameAttributeId(event.target.value)
    }

    const onSelectAssetXCoordinate = (event: SelectChangeEvent<string>) => {
        setAssetXCoordinateAttributeId(event.target.value)
    }
    const onSelectAssetYCoordinate = (event: SelectChangeEvent<string>) => {
        setAssetYCoordinateAttributeId(event.target.value)
    }

    const onSelectReportId = (event: SelectChangeEvent<string>) => {
        setReportIdAttributeId(event.target.value)
    }

    const onSelectReportEntity = (event: SelectChangeEvent<string>) => {
        setReportEntityTemplateId(event.target.value)
        setReportDataFromAttributeId('')
        setReportDataUntilAttributeId('')
        setReportingObjectAttributeId('')
        setReportURLAttributeId('')
    }

    const onSelectReportDataFrom = (event: SelectChangeEvent<string>) => {
        setReportDataFromAttributeId(event.target.value)
    }

    const onSelectReportDataUntil = (event: SelectChangeEvent<string>) => {
        setReportDataUntilAttributeId(event.target.value)
    }

    const onSelectReportingObject = (event: SelectChangeEvent<string>) => {
        setReportingObjectAttributeId(event.target.value)
    }

    const onSelectReportURL = (event: SelectChangeEvent<string>) => {
        setReportURLAttributeId(event.target.value)
    }

    const getOptions = (relations: IRelation[]) => {
        return relations.reduce((acc: EntityType[], relation) => {
            const relatedEntityTemplate = entityTypes.find(
                (entity) => entity.id === relation.childEntity,
            )
            if (relatedEntityTemplate) {
                acc.push(relatedEntityTemplate)
            }
            return acc
        }, [])
    }

    // When there are 0 levels before building, there is no parent template
    // so we can't derive options from relations — fall back to all entityTypes
    const buildingEntityTemplateOptions = siteEntityTemplateId
        ? flattenEntityTemplates(getOptions(siteRelations))
        : entityTypes

    const assetEntityTemplateOptions = flattenEntityTemplates(
        getOptions(buildingRelations),
    )

    const sensorEntityTemplateOptions = flattenEntityTemplates(
        getOptions(assetRelations),
    )

    const handleSave = async () => {
        const facilityMonitoringSettings: IFacilityMonitoringSettingsType = {
            enable: enable.toString(),
            analyticsDashboardId,
            scheduledReportsEntityTemplateId,
            reportEntityTemplateId,
            reportDataFromAttributeId,
            reportDataUntilAttributeId,
            reportingObjectAttributeId,
            reportURLAttributeId,
            sensorEntityTemplateId,
            sensorReadingAttributeId,
            numberOfLevelsBeforeBuilding: numLevels.toString(),
            siteLocationAttributeId,
            siteNameAttributeId,
            siteEntityTemplateId,
            buildingEntityTemplateId,
            buildingNameAttributeId,
            buildingFloorLayoutAttributeId,
            assetEntityTemplateId,
            assetNameAttributeId,
            assetXCoordinateAttributeId,
            assetYCoordinateAttributeId,
            reportIdAttributeId,
            preSelectedChartableAttributes,
        }

        await updateApplicationSettings<IFacilityMonitoringSettingsType>(
            orgId,
            Applications.facilityMonitoring.id,
            facilityMonitoringSettings,
        )

        enqueueSnackbar(t('settingsSavedSuccess'), { variant: 'success' })
        dispatch(incrementSettingsVersion())
    }

    useEffect(() => {
        const getSettings = async () => {
            const response =
                await getApplicationSettings<IFacilityMonitoringSettingsType>(
                    orgId,
                    Applications.facilityMonitoring.id,
                )
            const settings = response?.appSettings
            if (settings) {
                setEnable(settings.enable === 'true')
                setAnalyticsDashboardId(settings.analyticsDashboardId)
                setScheduledReportsEntityTemplateId(
                    settings.scheduledReportsEntityTemplateId || '',
                )
                setReportEntityTemplateId(settings.reportEntityTemplateId || '')
                setSensorEntityTemplateId(settings.sensorEntityTemplateId || '')
                setSensorReadingAttributeId(
                    settings.sensorReadingAttributeId || '',
                )
                setNumLevels(
                    Number.parseInt(
                        settings.numberOfLevelsBeforeBuilding || '0',
                        10,
                    ),
                )
                setSiteLocationAttributeId(
                    settings.siteLocationAttributeId || '',
                )
                setSiteNameAttributeId(settings.siteNameAttributeId || '')
                setBuildingEntityTemplateId(
                    settings.buildingEntityTemplateId || '',
                )
                setBuildingNameAttributeId(
                    settings.buildingNameAttributeId || '',
                )
                setBuildingFloorLayoutAttributeId(
                    settings.buildingFloorLayoutAttributeId || '',
                )
                setAssetEntityTemplateId(settings.assetEntityTemplateId || '')
                setAssetNameAttributeId(settings.assetNameAttributeId || '')
                setAssetXCoordinateAttributeId(
                    settings.assetXCoordinateAttributeId || '',
                )
                setAssetYCoordinateAttributeId(
                    settings.assetYCoordinateAttributeId || '',
                )
                setReportIdAttributeId(settings.reportIdAttributeId || '')
                setReportDataFromAttributeId(
                    settings.reportDataFromAttributeId || '',
                )
                setReportDataUntilAttributeId(
                    settings.reportDataUntilAttributeId || '',
                )
                setReportingObjectAttributeId(
                    settings.reportingObjectAttributeId || '',
                )
                setReportURLAttributeId(settings.reportURLAttributeId || '')
                setPreSelectedChartableAttributes(
                    settings.preSelectedChartableAttributes || '[]',
                )
            }

            if (response?.hierarchy) {
                setHierarchy(response.hierarchy)
                if (response.hierarchy.length > 0) {
                    setSiteEntityTemplateId(
                        response.hierarchy[response.hierarchy.length - 1]
                            .entityTemplateId,
                    )
                }
            }
        }
        getSettings()
    }, [orgId])

    /**
     * TODO: Transform the dropdowns below similary using GenericDropDown component as used in InTransit settings.
     */

    return (
        <Box
            sx={{
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
            }}
        >
            <Box
                sx={{
                    width: '500px',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('enableFacilityMonitoring')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <Switch
                            id="fm-settings-switch-enable"
                            checked={enable}
                            onChange={(event) =>
                                setEnable(event.target.checked)
                            }
                            inputProps={{
                                'aria-label': t('enableFacilityMonitoring'),
                            }}
                            disabled={!hasWriteAccess}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('biDashboardId')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <TextField
                            id="analyticsDashboardId"
                            name="analyticsDashboardId"
                            label={t('biDashboardId')}
                            variant="outlined"
                            value={analyticsDashboardId}
                            onChange={onChangeAnalyticsDashboardId}
                            fullWidth
                            autoComplete="off"
                            disabled={!hasWriteAccess}
                            InputLabelProps={{
                                sx: {
                                    backgroundColor: '#fff',
                                    px: 1,
                                    borderRadius: 1,
                                },
                            }}
                        />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('numberOfLevelsBeforeBuilding')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <FormControl fullWidth disabled={!hasWriteAccess}>
                            <InputLabel
                                id="num-levels-label"
                                sx={{
                                    backgroundColor: '#fff',
                                    px: 1,
                                    borderRadius: 1,
                                }}
                            >
                                {t('numberOfLevels')}
                            </InputLabel>
                            <Select
                                labelId="num-levels-label"
                                value={numLevels}
                                onChange={(e) =>
                                    setNumLevels(Number(e.target.value))
                                }
                            >
                                {Array.from({
                                    length: (hierarchy?.length || 0) + 1,
                                }).map((_, i) => (
                                    <MenuItem key={i} value={i} dense>
                                        {i}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('siteEntityTemplate')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <FormControl
                            fullWidth
                            disabled={
                                !hasWriteAccess ||
                                (hierarchy && hierarchy.length > 0)
                            }
                        >
                            <InputLabel
                                id="site-entity-template"
                                htmlFor="site-entity-template-select"
                                sx={{
                                    backgroundColor: '#fff',
                                    px: 1,
                                    borderRadius: 1,
                                }}
                            >
                                {t('siteEntityTemplate')}
                            </InputLabel>
                            <Select
                                id="site-entity-template-select"
                                labelId="site-entity-template"
                                label={t('siteEntityTemplate')}
                                value={siteEntityTemplateId}
                                onChange={onSelectSite}
                                defaultValue={siteEntityTemplateId}
                            >
                                {entityTypes?.map((option) => (
                                    <MenuItem
                                        id={`fm-settings-menuitem-site-template-${option?.id}`}
                                        key={option?.id}
                                        value={option?.id}
                                        dense
                                    >
                                        {option?.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                {numLevels > 0 && siteEntityTemplateId && (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{ marginRight: '8px', width: '200px' }}
                            >
                                {t('siteLocationAttribute')}
                            </Typography>
                            <Box sx={{ width: '300px' }}>
                                <FormControl
                                    fullWidth
                                    disabled={!hasWriteAccess}
                                >
                                    <InputLabel
                                        id="site-location-attribute"
                                        htmlFor="site-location-attribute-select"
                                        sx={{
                                            backgroundColor: '#fff',
                                            px: 1,
                                            borderRadius: 1,
                                        }}
                                    >
                                        {t('siteLocationAttribute')}
                                    </InputLabel>
                                    <Select
                                        id="site-location-attribute-select"
                                        labelId="site-location-attribute"
                                        label={t('siteLocationAttribute')}
                                        value={siteLocationAttributeId}
                                        onChange={onSelectLocation}
                                        defaultValue={siteLocationAttributeId}
                                    >
                                        {siteAttributeOptions?.map((option) => (
                                            <MenuItem
                                                id={`fm-settings-menuitem-site-location-${option.id}`}
                                                key={option.id}
                                                value={option.id}
                                                dense
                                            >
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{ marginRight: '8px', width: '200px' }}
                            >
                                {t('siteNameAttribute')}
                            </Typography>
                            <Box sx={{ width: '300px' }}>
                                <FormControl
                                    fullWidth
                                    disabled={!hasWriteAccess}
                                >
                                    <InputLabel
                                        id="site-name-attribute"
                                        htmlFor="site-name-attribute-select"
                                        sx={{
                                            backgroundColor: '#fff',
                                            px: 1,
                                            borderRadius: 1,
                                        }}
                                    >
                                        {t('siteNameAttribute')}
                                    </InputLabel>
                                    <Select
                                        id="site-name-attribute-select"
                                        labelId="site-name-attribute"
                                        label={t('siteNameAttribute')}
                                        value={siteNameAttributeId}
                                        onChange={onSelectSiteName}
                                        defaultValue={siteNameAttributeId}
                                    >
                                        {siteAttributes?.map((option) => (
                                            <MenuItem
                                                id={`fm-settings-menuitem-site-name-${option.id}`}
                                                key={option.id}
                                                value={option.id}
                                                dense
                                            >
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    </>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('buildingEntityTemplate')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <FormControl fullWidth disabled={!hasWriteAccess}>
                            <InputLabel
                                id="building-entity-template"
                                htmlFor="building-entity-template-select"
                                sx={{
                                    backgroundColor: '#fff',
                                    px: 1,
                                    borderRadius: 1,
                                }}
                            >
                                {t('buildingEntityTemplate')}
                            </InputLabel>
                            <Select
                                id="building-entity-template-select"
                                labelId="building-entity-template"
                                label={t('buildingEntityTemplate')}
                                value={buildingEntityTemplateId}
                                onChange={onSelectBuilding}
                                defaultValue={buildingEntityTemplateId}
                            >
                                {buildingEntityTemplateOptions?.map(
                                    (option) => (
                                        <MenuItem
                                            id={`fm-settings-menuitem-building-template-${option?.id}`}
                                            key={option?.id}
                                            value={option?.id}
                                            dense
                                        >
                                            {option?.name}
                                        </MenuItem>
                                    ),
                                )}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                {buildingEntityTemplateId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ marginRight: '8px', width: '200px' }}>
                            {t('buildingNameAttribute')}
                        </Typography>
                        <Box sx={{ width: '300px' }}>
                            <FormControl fullWidth disabled={!hasWriteAccess}>
                                <InputLabel
                                    id="building-name-attribute"
                                    htmlFor="building-name-attribute-select"
                                >
                                    {t('buildingNameAttribute')}
                                </InputLabel>
                                <Select
                                    id="building-name-attribute-select"
                                    labelId="building-name-attribute"
                                    label={t('buildingNameAttribute')}
                                    value={buildingNameAttributeId}
                                    onChange={onSelectBuildingName}
                                    defaultValue={buildingNameAttributeId}
                                >
                                    {buildingAttributes?.map((option) => (
                                        <MenuItem
                                            id={`fm-settings-menuitem-building-${option.id}`}
                                            key={option.id}
                                            value={option.id}
                                            dense
                                        >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}
                {buildingEntityTemplateId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ marginRight: '8px', width: '200px' }}>
                            {t('buildingFloorLayoutAttribute')}
                        </Typography>
                        <Box sx={{ width: '300px' }}>
                            <FormControl fullWidth disabled={!hasWriteAccess}>
                                <InputLabel
                                    id="building-floor-layout-attribute"
                                    htmlFor="building-floor-layout-attribute-select"
                                >
                                    {t('buildingFloorLayoutAttribute')}
                                </InputLabel>
                                <Select
                                    id="building-floor-layout-attribute-select"
                                    labelId="building-floor-layout-attribute"
                                    label={t('buildingFloorLayoutAttribute')}
                                    value={buildingFloorLayoutAttributeId}
                                    onChange={onSelectBuildingFloorLayout}
                                    defaultValue={
                                        buildingFloorLayoutAttributeId
                                    }
                                >
                                    {buildingAttributes?.map((option) => (
                                        <MenuItem
                                            id={`fm-settings-menuitem-building-floor-${option.id}`}
                                            key={option.id}
                                            value={option.id}
                                            dense
                                        >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}
                {buildingEntityTemplateId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ marginRight: '8px', width: '200px' }}>
                            {t('assetEntityTemplate')}
                        </Typography>
                        <Box sx={{ width: '300px' }}>
                            <FormControl fullWidth disabled={!hasWriteAccess}>
                                <InputLabel
                                    id="asset-entity-template"
                                    htmlFor="asset-entity-template-select"
                                >
                                    {t('assetEntityTemplate')}
                                </InputLabel>
                                <Select
                                    id="asset-entity-template-select"
                                    labelId="asset-entity-template"
                                    label={t('assetEntityTemplate')}
                                    value={assetEntityTemplateId}
                                    onChange={onSelectAssetEntity}
                                    defaultValue={assetEntityTemplateId}
                                >
                                    {assetEntityTemplateOptions?.map(
                                        (option) => (
                                            <MenuItem
                                                id={`fm-settings-menuitem-asset-template-${option?.id}`}
                                                key={option?.id}
                                                value={option?.id}
                                                dense
                                            >
                                                {option?.name}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}
                {assetEntityTemplateId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ marginRight: '8px', width: '200px' }}>
                            {t('assetNameAttribute')}
                        </Typography>
                        <Box sx={{ width: '300px' }}>
                            <FormControl fullWidth disabled={!hasWriteAccess}>
                                <InputLabel
                                    id="asset-name-attribute"
                                    htmlFor="asset-name-attribute-select"
                                >
                                    {t('assetNameAttribute')}
                                </InputLabel>
                                <Select
                                    id="asset-name-attribute-select"
                                    labelId="asset-name-attribute"
                                    label={t('assetEntityTemplate')}
                                    value={assetNameAttributeId}
                                    onChange={onSelectAssetAttribute}
                                    defaultValue={assetNameAttributeId}
                                >
                                    {assetAttributes?.map((option) => (
                                        <MenuItem
                                            id={`fm-settings-menuitem-asset-${option.id}`}
                                            key={option.id}
                                            value={option.id}
                                            dense
                                        >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}
                {assetEntityTemplateId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ marginRight: '8px', width: '200px' }}>
                            {t('assetXCoordinateAttribute')}
                        </Typography>
                        <Box sx={{ width: '300px' }}>
                            <FormControl fullWidth disabled={!hasWriteAccess}>
                                <InputLabel
                                    id="asset-x-coordinate-attribute"
                                    htmlFor="asset-x-coordinate-attribute-select"
                                >
                                    {t('assetXCoordinateAttribute')}
                                </InputLabel>
                                <Select
                                    id="asset-x-coordinate-attribute-select"
                                    labelId="asset-x-coordinate-attribute"
                                    label={t('assetXCoordinateAttribute')}
                                    value={assetXCoordinateAttributeId}
                                    onChange={onSelectAssetXCoordinate}
                                    defaultValue={assetXCoordinateAttributeId}
                                >
                                    {assetAttributes?.map((option) => (
                                        <MenuItem
                                            id={`fm-settings-menuitem-asset-x-${option.id}`}
                                            key={option.id}
                                            value={option.id}
                                            dense
                                        >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}
                {assetEntityTemplateId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ marginRight: '8px', width: '200px' }}>
                            {t('assetYCoordinateAttribute')}
                        </Typography>
                        <Box sx={{ width: '300px' }}>
                            <FormControl fullWidth disabled={!hasWriteAccess}>
                                <InputLabel
                                    id="asset-y-coordinate-attribute"
                                    htmlFor="asset-y-coordinate-attribute-select"
                                >
                                    {t('assetYCoordinateAttribute')}
                                </InputLabel>
                                <Select
                                    id="asset-y-coordinate-attribute-select"
                                    labelId="asset-y-coordinate-attribute"
                                    label={t('assetYCoordinateAttribute')}
                                    value={assetYCoordinateAttributeId}
                                    onChange={onSelectAssetYCoordinate}
                                    defaultValue={assetYCoordinateAttributeId}
                                >
                                    {assetAttributes?.map((option) => (
                                        <MenuItem
                                            id={`fm-settings-menuitem-asset-y-${option.id}`}
                                            key={option.id}
                                            value={option.id}
                                            dense
                                        >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}
                {assetEntityTemplateId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ marginRight: '8px', width: '200px' }}>
                            {t('reportIdAttribute')}
                        </Typography>
                        <Box sx={{ width: '300px' }}>
                            <FormControl fullWidth disabled={!hasWriteAccess}>
                                <InputLabel
                                    id="report-id-attribute"
                                    htmlFor="report-id-attribute-select"
                                >
                                    {t('reportIdAttribute')}
                                </InputLabel>
                                <Select
                                    id="report-id-attribute-select"
                                    labelId="report-id-attribute"
                                    label={t('reportIdAttribute')}
                                    value={reportIdAttributeId}
                                    onChange={onSelectReportId}
                                    defaultValue={reportIdAttributeId}
                                >
                                    {assetAttributes?.map((option) => (
                                        <MenuItem
                                            id={`fm-settings-menuitem-report-id-${option.id}`}
                                            key={option.id}
                                            value={option.id}
                                            dense
                                        >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}

                {assetEntityTemplateId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ marginRight: '8px', width: '200px' }}>
                            {t('sensorEntityTemplate')}
                        </Typography>
                        <Box sx={{ width: '300px' }}>
                            <FormControl fullWidth disabled={!hasWriteAccess}>
                                <InputLabel
                                    id="sensor-entity-template"
                                    htmlFor="sensor-entity-template-select"
                                >
                                    {t('sensorEntityTemplate')}
                                </InputLabel>
                                <Select
                                    id="sensor-entity-template-select"
                                    labelId="sensor-entity-template"
                                    label={t('sensorEntityTemplate')}
                                    value={sensorEntityTemplateId}
                                    onChange={onSelectSensor}
                                    defaultValue={sensorEntityTemplateId}
                                >
                                    {sensorEntityTemplateOptions?.map(
                                        (option) => (
                                            <MenuItem
                                                id={`fm-settings-menuitem-sensor-${option?.id}`}
                                                key={option?.id}
                                                value={option?.id}
                                                dense
                                            >
                                                {option?.name}
                                            </MenuItem>
                                        ),
                                    )}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}
                {sensorEntityTemplateId && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography sx={{ marginRight: '8px', width: '200px' }}>
                            {t('sensorReadingAttribute')}
                        </Typography>
                        <Box sx={{ width: '300px' }}>
                            <FormControl fullWidth disabled={!hasWriteAccess}>
                                <InputLabel
                                    id="sensor-reading-attribute"
                                    htmlFor="sensor-reading-attribute-select"
                                >
                                    {t('sensorReadingAttribute')}
                                </InputLabel>
                                <Select
                                    id="sensor-reading-attribute-select"
                                    labelId="sensor-reading-attribute"
                                    label={t('sensorReadingAttribute')}
                                    value={sensorReadingAttributeId}
                                    onChange={onSelectSensorReading}
                                    defaultValue={sensorReadingAttributeId}
                                >
                                    {sensorAttributes?.map((option) => (
                                        <MenuItem
                                            id={`fm-settings-menuitem-sensor-reading-${option.id}`}
                                            key={option.id}
                                            value={option.id}
                                            dense
                                        >
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('scheduledReportsEntityTemplate')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <FormControl fullWidth disabled={!hasWriteAccess}>
                            <InputLabel
                                id="scheduled-reports-entity-template"
                                htmlFor="scheduled-reports-entity-template-select"
                            >
                                {t('scheduledReportsEntityTemplate')}
                            </InputLabel>
                            <Select
                                id="scheduled-reports-entity-template-select"
                                labelId="scheduled-reports-entity-template"
                                label={t('scheduledReportsEntityTemplate')}
                                value={scheduledReportsEntityTemplateId}
                                onChange={(e) =>
                                    setScheduledReportsEntityTemplateId(
                                        e.target.value,
                                    )
                                }
                                defaultValue={scheduledReportsEntityTemplateId}
                                sx={{
                                    '& .MuiSelect-select': {
                                        width: 'calc(100% - 40px)',
                                        maxWidth: 'calc(100% - 40px)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                    },
                                    '& .MuiInputBase-input': {
                                        width: 'calc(100% - 40px)',
                                        maxWidth: 'calc(100% - 40px)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        display: 'block',
                                    },
                                    '& .MuiInputLabel-root': {
                                        width: 'calc(100% - 40px)',
                                        maxWidth: 'calc(100% - 40px)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        width: 'auto',
                                        maxWidth: 'none',
                                        overflow: 'visible',
                                        textOverflow: 'initial',
                                        whiteSpace: 'normal',
                                    },
                                }}
                            >
                                {entityTypes?.map((option) => (
                                    <MenuItem
                                        id={`fm-settings-select-scheduled-reports-${option.id}`}
                                        key={option.id}
                                        value={option.id}
                                        dense
                                    >
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('reportEntityTemplate')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <FormControl fullWidth disabled={!hasWriteAccess}>
                            <InputLabel
                                id="report-entity-template"
                                htmlFor="report-entity-template-select"
                            >
                                {t('reportEntityTemplate')}
                            </InputLabel>
                            <Select
                                id="report-entity-template-select"
                                labelId="report-entity-template"
                                label={t('reportEntityTemplate')}
                                title={t('reportEntityTemplate')}
                                value={reportEntityTemplateId}
                                onChange={onSelectReportEntity}
                            >
                                {entityTypes?.map((option) => (
                                    <MenuItem
                                        id={`fm-settings-select-report-template-${option.id}`}
                                        key={option.id}
                                        value={option.id}
                                        dense
                                    >
                                        {option.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                {reportEntityTemplateId && (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{ marginRight: '8px', width: '200px' }}
                            >
                                {t('reportDataFromAttribute')}
                            </Typography>
                            <Box sx={{ width: '300px' }}>
                                <FormControl
                                    fullWidth
                                    disabled={!hasWriteAccess}
                                >
                                    <InputLabel
                                        id="report-data-from-attribute"
                                        htmlFor="report-data-from-attribute-select"
                                    >
                                        {t('reportDataFromAttribute')}
                                    </InputLabel>
                                    <Select
                                        id="report-data-from-attribute-select"
                                        labelId="report-data-from-attribute"
                                        label={t('reportDataFromAttribute')}
                                        value={reportDataFromAttributeId}
                                        onChange={onSelectReportDataFrom}
                                        defaultValue={reportDataFromAttributeId}
                                    >
                                        {reportsAttributes?.map((option) => (
                                            <MenuItem
                                                id={`fm-settings-menuitem-report-from-${option.id}`}
                                                key={option.id}
                                                value={option.id}
                                                dense
                                            >
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{ marginRight: '8px', width: '200px' }}
                            >
                                {t('reportDataUntilAttribute')}
                            </Typography>
                            <Box sx={{ width: '300px' }}>
                                <FormControl
                                    fullWidth
                                    disabled={!hasWriteAccess}
                                >
                                    <InputLabel
                                        id="report-data-until-attribute"
                                        htmlFor="report-data-until-attribute-select"
                                    >
                                        {t('reportDataUntilAttribute')}
                                    </InputLabel>
                                    <Select
                                        id="report-data-until-attribute-select"
                                        labelId="report-data-until-attribute"
                                        label={t('reportDataUntilAttribute')}
                                        title={t('reportDataUntilAttribute')}
                                        value={reportDataUntilAttributeId}
                                        onChange={onSelectReportDataUntil}
                                    >
                                        {reportsAttributes?.map((option) => (
                                            <MenuItem
                                                id={`fm-settings-menuitem-report-until-${option.id}`}
                                                key={option.id}
                                                value={option.id}
                                                dense
                                            >
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{ marginRight: '8px', width: '200px' }}
                            >
                                {t('reportingObjectAttribute')}
                            </Typography>
                            <Box sx={{ width: '300px' }}>
                                <FormControl
                                    fullWidth
                                    disabled={!hasWriteAccess}
                                >
                                    <InputLabel
                                        id="reporting-object-attribute"
                                        htmlFor="reporting-object-attribute-select"
                                    >
                                        {t('reportingObjectAttribute')}
                                    </InputLabel>
                                    <Select
                                        id="reporting-object-attribute-select"
                                        labelId="reporting-object-attribute"
                                        label={t('reportingObjectAttribute')}
                                        value={reportingObjectAttributeId}
                                        onChange={onSelectReportingObject}
                                        defaultValue={
                                            reportingObjectAttributeId
                                        }
                                    >
                                        {reportsAttributes?.map((option) => (
                                            <MenuItem
                                                id={`fm-settings-menuitem-report-object-${option.id}`}
                                                key={option.id}
                                                value={option.id}
                                                dense
                                            >
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 2,
                            }}
                        >
                            <Typography
                                sx={{ marginRight: '8px', width: '200px' }}
                            >
                                {t('reportURLAttribute')}
                            </Typography>
                            <Box sx={{ width: '300px' }}>
                                <FormControl
                                    fullWidth
                                    disabled={!hasWriteAccess}
                                >
                                    <InputLabel
                                        id="report-url-attribute"
                                        htmlFor="report-url-attribute-select"
                                    >
                                        {t('reportURLAttribute')}
                                    </InputLabel>
                                    <Select
                                        id="report-url-attribute-select"
                                        labelId="report-url-attribute"
                                        label={t('reportURLAttribute')}
                                        value={reportURLAttributeId}
                                        onChange={onSelectReportURL}
                                        defaultValue={reportURLAttributeId}
                                    >
                                        {reportsAttributes?.map((option) => (
                                            <MenuItem
                                                id={`fm-settings-menuitem-report-url-${option.id}`}
                                                key={option.id}
                                                value={option.id}
                                                dense
                                            >
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Box>
                    </>
                )}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >
                    <Typography sx={{ marginRight: '8px', width: '200px' }}>
                        {t('preSelectedChartableAttributes')}
                    </Typography>
                    <Box sx={{ width: '300px' }}>
                        <MultiSelectInput
                            id="pre-selected-chartable-attributes"
                            label={t('preSelectedChartableAttributes')}
                            value={parsedPreSelectedChartableAttributes}
                            onChange={(id: string, value: string[]) => {
                                setPreSelectedChartableAttributes(
                                    JSON.stringify(value),
                                )
                            }}
                            disabled={!hasWriteAccess}
                            list={preSelectableChartableAttributesOptions.map(
                                (i) => {
                                    return {
                                        id: i.id,
                                        name: i.name,
                                    }
                                },
                            )}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
                >
                    {hasWriteAccess && (
                        <Button
                            id="fm-settings-button-save"
                            variant="contained"
                            onClick={handleSave}
                            sx={{
                                backgroundColor: '#3a5e98',
                                color: '#ffffff',
                            }}
                        >
                            {t('save')}
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export default FacilityMonitoringSettings
